import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseHolidayDisplayDate } from "../../util/managementHelpers";

const isSameDay = (left, right) =>
  left &&
  right &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const buildDayTooltip = (date, holiday, leaveTooltip) => {
  const parts = [];

  if (holiday) {
    parts.push(`Holiday: ${holiday.name} (${holiday.day})`);
  }

  if (leaveTooltip) {
    parts.push(leaveTooltip);
  }

  return parts.length ? parts.join("\n") : undefined;
};

const ManagementCalendar = ({
  holidays = [],
  dayStatusMap = {},
  dayTooltipMap = {},
  selectedDate,
  onChange,
  inline = true,
  minDate,
  maxDate,
}) => {
  const holidayMap = useMemo(() => {
    const map = new Map();

    holidays.forEach((holiday) => {
      const parsed = parseHolidayDisplayDate(holiday.date);

      if (parsed) {
        map.set(parsed.toDateString(), holiday);
      }
    });

    return map;
  }, [holidays]);

  const renderDayContents = (day, date) => {
    const holiday = holidayMap.get(date.toDateString());
    const dayKey = formatDateKey(date);
    const dayStatus = dayStatusMap[dayKey] || "";
    const leaveTooltip = dayTooltipMap[dayKey];
    const isToday = isSameDay(date, new Date());
    const showHoliday = Boolean(holiday) && !dayStatus;
    const showToday = isToday && !dayStatus && !showHoliday;
    const tooltip = buildDayTooltip(date, holiday, leaveTooltip);

    return (
      <span
        className={`management_Calendar_Day ${
          showHoliday ? "is-holiday" : ""
        } ${showToday ? "is-today" : ""} ${
          dayStatus ? `is-${dayStatus}` : ""
        }`}
        title={tooltip}
      >
        {day}
      </span>
    );
  };

  return (
    <div className="management_Calendar_Wrap">
      <DatePicker
        inline={inline}
        selected={selectedDate}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        renderDayContents={renderDayContents}
      />
      <div className="management_Calendar_Legend">
        <span className="legend today">Today</span>
        <span className="legend holiday">Holiday</span>
        <span className="legend leave-pending">Leave requested</span>
        <span className="legend leave-approved">Leave approved</span>
        <span className="legend present">Present (past dates)</span>
      </div>
      {selectedDate && holidayMap.get(selectedDate.toDateString()) && (
        <div className="management_Holiday_Detail">
          <strong>Holiday:</strong>{" "}
          {holidayMap.get(selectedDate.toDateString()).name} —{" "}
          {holidayMap.get(selectedDate.toDateString()).day}
        </div>
      )}
    </div>
  );
};

export default ManagementCalendar;
